import { memo, useCallback, useEffect, useState } from "react";

import { useHandleNumberFieldChange, useHandleTextFieldChange } from "services/forms/hooks";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { TextArea } from "ui/components/TextField/TextArea";
import NumberField from "ui/components/TextField/NumberField/NumberField";

import { initialCustomEquipmentDraft } from "../consts";
import { CustomEquipmentDraft } from "../types";

interface EquipmentAddModalProps {
  show: boolean;
  onSave: (item: CustomEquipmentDraft) => void;
  onClose: () => void;
}

/** Modal za ručno dodavanje vlastite dodatne opreme vozila. */
const EquipmentAddModal: React.FC<EquipmentAddModalProps> = (props) => {
  const { show, onSave, onClose } = props;

  const [draft, setDraft] = useState<CustomEquipmentDraft>(initialCustomEquipmentDraft);
  const [error, setError] = useState<string>("");

  // Reset na otvaranje
  useEffect(() => {
    if (!show) return;
    setDraft(initialCustomEquipmentDraft);
    setError("");
  }, [show]);

  const handleTextChange = useHandleTextFieldChange(setDraft, draft);
  const handleNumberChange = useHandleNumberFieldChange(setDraft, draft);

  const handleSave = useCallback(() => {
    const name = draft.name.trim();
    if (!name) {
      setError("Molimo unesite naziv opreme");
      return;
    }

    onSave({
      name,
      description: draft.description.trim(),
      priceEur: draft.priceEur,
    });
  }, [draft, onSave]);

  return (
    <Modal show={show} position="centered" size="small" onHide={onClose}>
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title="Dodaj opremu"
        onHide={onClose}
      />
      <ModalBody>
        <div className="px-4 pt-5 pb-8 space-y-4">
          <div>
            <TextField
              type="text"
              name="name"
              label="Naziv opreme"
              placeholder="npr. Vučna kuka"
              value={draft.name}
              onChange={handleTextChange}
              error={error}
              required
            />
          </div>
          <div>
            <TextArea
              rows={2}
              name="description"
              label="Opis"
              placeholder="Dodatni opis opreme (nije obavezno)"
              value={draft.description}
              onChange={handleTextChange}
            />
          </div>
          <div>
            <NumberField
              name="priceEur"
              label="Cijena EUR"
              value={draft.priceEur}
              onChange={handleNumberChange}
              groupAppend="€"
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
          <Button variant="outlined" onClick={onClose}>
            Odustani
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Dodaj opremu
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(EquipmentAddModal);
